/**
 * The agency's brand on every report cover and in the client portal: name,
 * accent colour and logo, with a live preview of a cover. The server checks
 * the logo again; this screen only stops the obvious mistakes early.
 */

import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { Branding } from '../portal/portal-api.ts';
import { agencyApi } from './agency-api.ts';
import { CoverPage } from './PageArt.tsx';

const MAX_LOGO_BYTES = 256 * 1024;
const ACCEPTED = ['image/png', 'image/jpeg', 'image/webp'];
const DEFAULT_ACCENT = '#1f8a85';

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(new Error('The file could not be read.'));
    reader.readAsDataURL(file);
  });
}

export function BrandingDialog({
  organisationId,
  onClose,
  onSaved,
}: {
  organisationId: string;
  onClose: () => void;
  onSaved: (branding: Branding) => void;
}): ReactNode {
  const [branding, setBranding] = useState<Branding | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const dialog = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    agencyApi
      .branding(organisationId)
      .then(setBranding)
      .catch(() => setProblem('Branding could not be loaded.'));
  }, [organisationId]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => event.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    dialog.current?.querySelector<HTMLInputElement>('input')?.focus();
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, branding !== null]);

  const pickLogo = async (file: File | undefined) => {
    if (!file || !branding) return;
    if (!ACCEPTED.includes(file.type))
      return setProblem('Use a PNG, JPEG or WebP image. SVG is not accepted.');
    if (file.size > MAX_LOGO_BYTES) return setProblem('The logo must be 256 KB or smaller.');
    setProblem(null);
    setBranding({ ...branding, logo: await readAsDataUrl(file) });
  };

  const save = async () => {
    if (!branding) return;
    setBusy(true);
    setProblem(null);
    try {
      onSaved(await agencyApi.saveBranding(organisationId, branding));
      onClose();
    } catch (error) {
      setProblem(error instanceof Error ? error.message : 'Branding could not be saved.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="rc-scrim"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <div
        ref={dialog}
        className="rc rbd"
        role="dialog"
        aria-modal="true"
        aria-labelledby="rbd-title"
      >
        <h2 id="rbd-title">Agency branding</h2>
        <p className="panel-note">
          Shown on every report cover and at the top of your clients' portal.
        </p>
        {problem && (
          <p className="rh-access-msg rh-access-error" role="alert">
            {problem}
          </p>
        )}
        {branding && (
          <div className="rbd-grid">
            <div className="rbd-fields">
              <label className="rs-field">
                Agency name
                <input
                  value={branding.display_name}
                  maxLength={120}
                  onChange={(event) =>
                    setBranding({ ...branding, display_name: event.target.value })
                  }
                />
              </label>
              <label className="rs-field">
                Accent colour
                <input
                  type="color"
                  value={branding.accent ?? DEFAULT_ACCENT}
                  onChange={(event) => setBranding({ ...branding, accent: event.target.value })}
                />
              </label>
              <label className="rs-field">
                Logo (PNG, JPEG or WebP, up to 256 KB)
                <input
                  type="file"
                  accept={ACCEPTED.join(',')}
                  onChange={(event) => void pickLogo(event.target.files?.[0])}
                />
              </label>
              {branding.logo && (
                <button
                  type="button"
                  className="rh-text-btn"
                  onClick={() => setBranding({ ...branding, logo: null })}
                >
                  Remove logo
                </button>
              )}
            </div>
            <div className="rbd-preview" aria-label="Cover preview">
              <CoverPage
                facts={{
                  reportName: 'Monthly marketing report',
                  clientName: 'Your client',
                  periodLabel: '1 Sep 2026 – 30 Sep 2026',
                  preparedBy: branding.display_name || 'Your agency',
                  logo: branding.logo,
                  accent: branding.accent,
                }}
              />
            </div>
          </div>
        )}
        <div className="rc-actions">
          <button type="button" className="btn secondary" onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className="btn"
            disabled={busy || !branding || !branding.display_name.trim()}
            onClick={() => void save()}
          >
            {busy ? 'Saving…' : 'Save branding'}
          </button>
        </div>
      </div>
    </div>
  );
}
